import React, { useState, useRef } from 'react';
import { priceBookAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Loader2, Upload, FileSpreadsheet, AlertTriangle, Check,
  ChevronDown, ChevronRight, Package, Wrench, FolderPlus,
  Copy, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';

const SOURCES = [
  {
    id: 'housecall_pro',
    name: 'Housecall Pro',
    description: 'Import from pricebook export',
    accept: '.csv',
    logo: '/crm-logos/hcp.png',
  },
  {
    id: 'markate',
    name: 'Markate',
    description: 'Import from items export',
    accept: '.csv',
    logo: '/crm-logos/markate.png',
  },
  {
    id: 'servicetitan',
    name: 'ServiceTitan',
    description: 'Import from pricebook export (.xlsx)',
    accept: '.xlsx',
    logo: '/crm-logos/servicetitan.png',
  },
  {
    id: 'jobber',
    name: 'Jobber',
    description: 'Import products & services',
    accept: '.csv',
    logo: '/crm-logos/jobber.png',
  },
];

export default function PriceBookImport({ onImported, autoOpen = false, preSelectSource = null }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('select'); // select | preview | result
  const [selectedSource, setSelectedSource] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const fileRef = useRef(null);

  // Support deep-link auto-open
  const hasAutoOpened = useRef(false);
  React.useEffect(() => {
    if (autoOpen && !hasAutoOpened.current) {
      hasAutoOpened.current = true;
      reset();
      setOpen(true);
    }
  }, [autoOpen]);

  const reset = () => {
    setStep('select');
    setSelectedSource(null);
    setFile(null);
    setPreview(null);
    setResult(null);
    setExpandedSections({});
  };

  const handleFileSelect = async (e, sourceId) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const src = SOURCES.find(s => s.id === sourceId);
    const allowedExt = src?.accept || '.csv';
    if (!f.name.toLowerCase().endsWith(allowedExt)) {
      toast.error(`Please select a ${allowedExt.toUpperCase()} file for ${src?.name || sourceId}`);
      return;
    }
    setFile(f);
    setSelectedSource(sourceId);
    setLoading(true);
    setStep('preview');

    try {
      const res = await priceBookAPI.previewImport(f, sourceId);
      setPreview(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to parse file');
      reset();
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file || !selectedSource) return;
    setLoading(true);
    try {
      const res = await priceBookAPI.executeImport(file, selectedSource);
      setResult(res.data);
      setStep('result');
      onImported?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => { reset(); setOpen(true); }}
        data-testid="import-pricebook-btn"
      >
        <Upload className="w-3.5 h-3.5 mr-1.5" /> Import
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!loading) { setOpen(o); if (!o) reset(); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="import-dialog">
          {/* Step 1: Source Selection */}
          {step === 'select' && (
            <>
              <DialogHeader>
                <DialogTitle>Import Price Book</DialogTitle>
                <DialogDescription>Import your catalog from a CRM export file</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {SOURCES.map((src) => (
                  <label
                    key={src.id}
                    className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-md hover:border-primary/50 ${preSelectSource === src.id ? 'border-primary bg-primary/5 shadow-md' : 'border-border'}`}
                    data-testid={`import-source-${src.id}`}
                  >
                    <div className="h-10 flex items-center justify-center">
                      <img src={src.logo} alt={src.name} className="max-h-10 max-w-[100px] object-contain" />
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-sm">{src.name}</div>
                      <div className="text-[11px] text-muted-foreground">{src.description}</div>
                    </div>
                    <input
                      type="file"
                      accept={src.accept}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      onChange={(e) => handleFileSelect(e, src.id)}
                    />
                  </label>
                ))}
              </div>
            </>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && (
            <>
              <DialogHeader>
                <DialogTitle>Import Preview</DialogTitle>
                <DialogDescription>
                  {file?.name} — {SOURCES.find(s => s.id === selectedSource)?.name} format
                </DialogDescription>
              </DialogHeader>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-sm text-muted-foreground">Analyzing file...</span>
                </div>
              ) : preview ? (
                <div className="space-y-4 mt-2">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-3 gap-2" data-testid="import-stats">
                    <StatCard icon={<Wrench className="w-4 h-4" />} label="Services" value={preview.stats.services} />
                    <StatCard icon={<Package className="w-4 h-4" />} label="Materials" value={preview.stats.materials} />
                    <StatCard icon={<FolderPlus className="w-4 h-4" />} label="New Categories" value={preview.stats.categories_to_create.length} />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <StatCard icon={<Check className="w-4 h-4 text-green-600" />} label="New Items" value={preview.stats.new_items} accent="green" />
                    <StatCard icon={<Copy className="w-4 h-4 text-amber-600" />} label="Duplicates (skip)" value={preview.stats.duplicates} accent="amber" />
                  </div>

                  {/* ServiceTitan sheet info */}
                  {preview.sheet_info && (
                    <div className="text-xs space-y-1 p-2.5 rounded-lg bg-sky-50 dark:bg-sky-950/20 border border-sky-200 dark:border-sky-800" data-testid="sheet-info">
                      {preview.sheet_info.processed.length > 0 && (
                        <div className="text-sky-700 dark:text-sky-300">
                          Sheets imported: {preview.sheet_info.processed.join(', ')}
                        </div>
                      )}
                      {preview.sheet_info.skipped.length > 0 && (
                        <div className="text-muted-foreground">
                          Skipped: {preview.sheet_info.skipped.join(', ')}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Categories to Create */}
                  {preview.stats.categories_to_create.length > 0 && (
                    <CollapsibleSection
                      title={`Categories to Create (${preview.stats.categories_to_create.length})`}
                      expanded={expandedSections.cats}
                      onToggle={() => toggleSection('cats')}
                    >
                      <div className="flex flex-wrap gap-1.5">
                        {preview.stats.categories_to_create.map((c, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{c}</Badge>
                        ))}
                      </div>
                    </CollapsibleSection>
                  )}

                  {/* Cleanup Suggestions */}
                  {(preview.cleanup.exact_duplicates.length > 0 ||
                    preview.cleanup.similar_items.length > 0 ||
                    preview.cleanup.category_suggestions.length > 0) && (
                    <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800" data-testid="cleanup-suggestions">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                        <span className="font-semibold text-sm text-amber-800 dark:text-amber-300">Cleanup Suggestions</span>
                        <span className="text-[10px] text-amber-600 dark:text-amber-400">(optional — won't block import)</span>
                      </div>

                      {preview.cleanup.exact_duplicates.length > 0 && (
                        <CollapsibleSection
                          title={`Exact Duplicates in CSV (${preview.cleanup.exact_duplicates.length})`}
                          expanded={expandedSections.exactDupes}
                          onToggle={() => toggleSection('exactDupes')}
                          small
                        >
                          <div className="space-y-1">
                            {preview.cleanup.exact_duplicates.map((d, i) => (
                              <div key={i} className="text-xs text-amber-700 dark:text-amber-400">
                                &quot;{d.name}&quot; appears {d.count}x in the file
                              </div>
                            ))}
                          </div>
                        </CollapsibleSection>
                      )}

                      {preview.cleanup.similar_items.length > 0 && (
                        <CollapsibleSection
                          title={`Similar Names (${preview.cleanup.similar_items.length})`}
                          expanded={expandedSections.similar}
                          onToggle={() => toggleSection('similar')}
                          small
                        >
                          <div className="space-y-1.5">
                            {preview.cleanup.similar_items.map((g, i) => (
                              <div key={i} className="text-xs">
                                {g.items.map((name, j) => (
                                  <span key={j}>
                                    {j > 0 && <ArrowRight className="w-3 h-3 inline mx-1 text-amber-500" />}
                                    <span className="text-amber-700 dark:text-amber-400">&quot;{name}&quot;</span>
                                  </span>
                                ))}
                              </div>
                            ))}
                          </div>
                        </CollapsibleSection>
                      )}

                      {preview.cleanup.category_suggestions.length > 0 && (
                        <CollapsibleSection
                          title={`Category Consolidation (${preview.cleanup.category_suggestions.length})`}
                          expanded={expandedSections.catSugg}
                          onToggle={() => toggleSection('catSugg')}
                          small
                        >
                          <div className="space-y-1">
                            {preview.cleanup.category_suggestions.map((s, i) => (
                              <div key={i} className="text-xs text-amber-700 dark:text-amber-400">
                                {s.variants.join(' / ')} — consider merging
                              </div>
                            ))}
                          </div>
                        </CollapsibleSection>
                      )}
                    </div>
                  )}

                  {/* Items Preview */}
                  <CollapsibleSection
                    title={`Items Preview (showing ${Math.min(preview.items_preview.length, 100)} of ${preview.parsed_items})`}
                    expanded={expandedSections.items}
                    onToggle={() => toggleSection('items')}
                  >
                    <div className="max-h-48 overflow-y-auto space-y-0.5">
                      {preview.items_preview.map((item, i) => (
                        <div
                          key={i}
                          className={`flex items-center justify-between text-xs py-1 px-2 rounded ${
                            item.is_duplicate ? 'bg-amber-50 dark:bg-amber-950/20 opacity-60' : 'bg-muted/50'
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <Badge className={`text-[8px] px-1 ${
                              item.item_type === 'material'
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                            }`}>
                              {item.item_type === 'material' ? 'MAT' : 'SVC'}
                            </Badge>
                            <span className="truncate font-medium">{item.name}</span>
                            <span className="text-[10px] text-muted-foreground shrink-0">{item.category}</span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0 ml-2">
                            {item.is_duplicate && (
                              <Badge variant="outline" className="text-[8px] border-amber-300 text-amber-600">SKIP</Badge>
                            )}
                            <span className="font-semibold">${item.price?.toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CollapsibleSection>

                  <DialogFooter>
                    <Button variant="outline" onClick={reset} disabled={loading}>Back</Button>
                    <Button
                      onClick={handleImport}
                      disabled={loading || preview.stats.new_items === 0}
                      data-testid="execute-import-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      Import {preview.stats.new_items} Items
                    </Button>
                  </DialogFooter>
                </div>
              ) : null}
            </>
          )}

          {/* Step 3: Result */}
          {step === 'result' && result && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" /> Import Complete
                </DialogTitle>
                <DialogDescription>{result.filename}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 mt-2" data-testid="import-result">
                <div className="grid grid-cols-2 gap-2">
                  <StatCard icon={<Check className="w-4 h-4 text-green-600" />} label="Items Created" value={result.summary.items_created} accent="green" />
                  <StatCard icon={<Copy className="w-4 h-4 text-amber-600" />} label="Duplicates Skipped" value={result.summary.items_skipped} accent="amber" />
                  <StatCard icon={<FolderPlus className="w-4 h-4" />} label="Categories Created" value={result.summary.categories_created} />
                  <StatCard icon={<FileSpreadsheet className="w-4 h-4" />} label="Total Rows" value={result.summary.total_rows} />
                </div>
                <div className="text-xs text-muted-foreground">
                  Batch ID: <code className="bg-muted px-1 rounded">{result.batch_id}</code>
                </div>
                <DialogFooter>
                  <Button onClick={() => { setOpen(false); reset(); }} data-testid="import-done-btn">Done</Button>
                </DialogFooter>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function StatCard({ icon, label, value, accent }) {
  const colors = accent === 'green'
    ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800'
    : accent === 'amber'
    ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800'
    : 'bg-muted/50 border-border';
  return (
    <div className={`p-3 rounded-lg border ${colors}`}>
      <div className="flex items-center gap-1.5 text-muted-foreground text-[10px] uppercase font-medium">
        {icon} {label}
      </div>
      <div className="text-xl font-bold mt-0.5">{value}</div>
    </div>
  );
}

function CollapsibleSection({ title, expanded, onToggle, children, small }) {
  return (
    <div className={small ? 'mt-1.5' : ''}>
      <button
        type="button"
        onClick={onToggle}
        className={`flex items-center gap-1 w-full text-left ${small ? 'text-xs' : 'text-xs font-semibold uppercase text-muted-foreground'}`}
      >
        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {title}
      </button>
      {expanded && <div className="mt-1 ml-4">{children}</div>}
    </div>
  );
}
